import React, { useState } from 'react';
import { Trans, Plural } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus, faCheckCircle } from '@fortawesome/pro-light-svg-icons';
import { Checkbox, isSalutatable, PhoneInput, SalutationAndTitleInputs, Toast, UserSalutation, UserTitle, Modal, ModalHeader, ModalBody, ModalFooter } from '@ygtechnologies/bygr-frontend-component-library';

import { ChangedItem, ResidentialUnit } from '@/types';

import { ValidatingInput, ValidationForm, ValidationSummary, ValidationScope } from '@/components/forms/validation';

import { Button } from '@/components/Buttons';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { useGetActiveProject, useValidationManager } from '@/utils/hooks';
import {
    inviteResidentialUnitBuyer,
    InviteBuyerOptions,
    getResidentialUnit,
    updateResidentialUnitBuyerInvitation,
} from '@/services/residential-units';
import { YesNoDialog } from '@/components/dialogs';

import theme from './ResidentialUnitInviteBuyerDialog.module.scss';

interface InvitationRowProps {
    value: InviteBuyerOptions;
    onChange(change: ChangedItem<InviteBuyerOptions>): void;
    onRemove(item: InviteBuyerOptions): void;
}

const InvitationRow: React.FC<InvitationRowProps> = ({ value, onChange, onRemove }) => {
    const project = useGetActiveProject();
    const validationManager = useValidationManager([
        {
            isValid: () => true,
            summaryMessage: ({ field }) => <Trans>Must select at least one delivery method</Trans>,
        },
    ]);

    const handleChange = (changes: Partial<InviteBuyerOptions>) => onChange(ChangedItem.fromMerge(value, changes));

    const handleRemove = () => {
        onRemove(value);
    };

    return (
        <ValidationScope validationManager={validationManager}>
            <div className="form columns-2 labels-30">
                {isSalutatable(project?.countryCode) && <SalutationAndTitleInputs value={value} onChange={handleChange} />}

                <label>
                    <Trans>First name</Trans>
                    <ValidatingInput
                        required
                        value={value.inviteeFirstName}
                        onChange={(e) => handleChange({ inviteeFirstName: e.target.value })}
                        placeholder={t`First name`}
                        className="form-control"
                    />
                </label>

                <label>
                    <Trans>Last name</Trans>
                    <ValidatingInput
                        required
                        value={value.inviteeLastName}
                        onChange={(e) => handleChange({ inviteeLastName: e.target.value })}
                        placeholder={t`Last name`}
                        className="form-control"
                    />
                </label>

                <label>
                    <Trans>Email</Trans>
                    <ValidatingInput
                        required
                        className="form-control"
                        placeholder={t`Email`}
                        value={value.inviteeEmail || ''}
                        onChange={(e) => handleChange({ inviteeEmail: e.target.value })}
                    />
                </label>

                <div className="form-label">
                    <Trans>SMS</Trans>
                    <div className="form-control-unstyled">
                        <PhoneInput
                            required={value.sendSms}
                            onChange={(value) => handleChange({ inviteePhoneNumber: value })}
                            fieldName={t`Phone number`}
                            value={value.inviteePhoneNumber || ''}
                            className="form-control"
                            initialCountry={project?.countryCode.toLowerCase()}
                        />
                    </div>
                </div>

                <div className="form-label">
                    <Trans>Invite via</Trans>
                    <div className="form-control-unstyled">
                        <Checkbox checked disabled inline>
                            <Trans>Email</Trans>
                        </Checkbox>
                        <Checkbox
                            inline
                            onChange={(e) => handleChange({ sendSms: e.target.checked })}
                            checked={value.sendSms}>
                            <Trans>SMS</Trans>
                        </Checkbox>
                    </div>
                </div>

                <div className="form-label">
                    <div className={theme.removeButtonWrapper}>
                        <Button color="danger" onClick={handleRemove}>
                            <Trans>Remove</Trans>
                        </Button>
                    </div>
                </div>

                <div>
                    <div className="label-offset">
                        <ValidationSummary />
                    </div>
                </div>
            </div>
        </ValidationScope>
    );
};

interface ResidentialUnitInviteBuyerDialogProps {
    residentialUnitId: number;
    show: boolean;
    onClose(): void;
    onAccept(options?: { invitationSent: boolean }): void;
    editMode?: boolean;
    existingInvitation?: {
        inviteId: number;
        firstName: string;
        lastName: string;
        email: string;
        phoneNumber: string;
        title: string | null;
        salutation: string | null;
    };
}

function getEmptyInvite(): InviteBuyerOptions {
    return {
        inviteeSalutation: null,
        inviteeTitle: null,
        inviteeFirstName: '',
        inviteeEmail: '',
        inviteePhoneNumber: '',
        inviteeLastName: '',
        sendSms: false,
    };
}

function getInviteFromExisting(
    existing: NonNullable<ResidentialUnitInviteBuyerDialogProps['existingInvitation']>
): InviteBuyerOptions {
    return {
        inviteeSalutation: existing.salutation as UserSalutation | null,
        inviteeTitle: existing.title as UserTitle | null,
        inviteeFirstName: existing.firstName,
        inviteeEmail: existing.email,
        inviteePhoneNumber: existing.phoneNumber,
        inviteeLastName: existing.lastName,
        sendSms: false,
    };
}

const ResidentialUnitInviteBuyerDialog: React.FC<ResidentialUnitInviteBuyerDialogProps> = ({
    residentialUnitId,
    show,
    onClose,
    onAccept,
    editMode = false,
    existingInvitation,
}) => {
    const [saving, setSaving] = useState(false);

    const [invites, setInvites] = useState<InviteBuyerOptions[]>([]);

    const validationManager = useValidationManager();
    const [residentialUnit, setResidentialUnit] = useState<ResidentialUnit | null>(null);

    const [isFinishedInviting, setIsFinishedInviting] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showSendInviteConfirmDialog, setShowSendInviteConfirmDialog] = useState(false);
    const [wasInvited, setWasInvited] = useState(true);

    const handleFormSubmit = async (e, shouldInvite?: boolean) => {
        e.preventDefault();
        if (editMode) {
            // Show confirmation dialog for edit mode
            setShowConfirmDialog(true);
        } else {
            // Show confirmation dialog for new invitations
            setShowSendInviteConfirmDialog(true);
        }
    };

    const handleAddWithoutInviting = async (e) => {
        e.preventDefault();
        sendRequest(false);
    };

    const sendRequest = async (shouldInvite?: boolean) => {
        setSaving(true);
        setWasInvited(shouldInvite ?? true);

        try {
            if (editMode && existingInvitation) {
                await updateResidentialUnitBuyerInvitation(
                    residentialUnitId,
                    existingInvitation.inviteId,
                    invites[0],
                    shouldInvite
                );
            } else {
                await inviteResidentialUnitBuyer(residentialUnitId, invites, shouldInvite);
            }
            triggerHandler(onAccept, { invitationSent: shouldInvite ?? true });
            setIsFinishedInviting(true);
        } catch (e: any) {
            if (e.status === 400) {
                Toast.error(<Trans>Invalid email address</Trans>);
            } else {
                throw e;
            }
        } finally {
            setSaving(false);
        }
    };

    const handleOpen = async () => {
        setIsFinishedInviting(false);
        setSaving(false);
        setWasInvited(true);
        if (editMode && existingInvitation) {
            setInvites([getInviteFromExisting(existingInvitation)]);
        } else {
            setInvites([getEmptyInvite()]);
        }
        setResidentialUnit(await getResidentialUnit(residentialUnitId));
    };

    const handleChange = (change: ChangedItem<InviteBuyerOptions>) => {
        setInvites(change.updateArray(invites));
    };

    const handleAdd = () => {
        setInvites([...invites, getEmptyInvite()]);
    };

    const handleRemove = (inv: InviteBuyerOptions) => {
        setInvites(invites.filter((x) => x !== inv));
    };

    const renderSuccess = () => {
        const iconSize = 100;

        return (
            <div className={theme.successContainer}>
                <FontAwesomeIcon style={{ width: `${iconSize}px`, height: `${iconSize}px` }} icon={faCheckCircle} />
                <p>
                    {editMode ? (
                        <>
                            <Trans>Successfully updated</Trans>{' '}
                            {invites.map((i) => `${i.inviteeFirstName} ${i.inviteeLastName}`).join(', ')}
                        </>
                    ) : wasInvited ? (
                        <>
                            <Trans>Successfully invited</Trans>{' '}
                            {invites.map((i) => `${i.inviteeFirstName} ${i.inviteeLastName}`).join(', ')}
                        </>
                    ) : (
                        <>
                            <Trans>Successfully added</Trans>{' '}
                            {invites.map((i) => `${i.inviteeFirstName} ${i.inviteeLastName}`).join(', ')}
                        </>
                    )}
                </p>
                <Button onClick={onClose} color="primary">
                    <Trans>Back to unit</Trans>
                </Button>
            </div>
        );
    };

    return (
        <Modal toggle={onClose} isOpen={show} onOpen={handleOpen} size="lg">
            {isFinishedInviting ? (
                renderSuccess()
            ) : (
                <ValidationForm onSubmit={handleFormSubmit} validationManager={validationManager}>
                    <ModalHeader>
                        {residentialUnit ? (
                            editMode ? (
                                <Trans>Edit buyer for </Trans>
                            ) : (
                                <Trans>Invite buyers to </Trans>
                            )
                        ) : null}
                        {residentialUnit ? <Trans>{residentialUnit.name}</Trans> : null}
                    </ModalHeader>

                    <ModalBody>
                        {invites.map((inv, index) => (
                            <div key={index} className={theme.inviteListItem}>
                                <InvitationRow value={inv} onChange={handleChange} onRemove={handleRemove} />
                            </div>
                        ))}
                    </ModalBody>

                    <ModalFooter>
                        <Button onClick={onClose} disabled={saving} color="tertiary" className="left">
                            <Trans>Cancel</Trans>
                        </Button>

                        {!editMode && (
                            <Button onClick={handleAdd} disabled={saving} color="tertiary" icon={faUserPlus}>
                                <Trans>Invite another buyer</Trans>
                            </Button>
                        )}

                        {!editMode && (
                            <Button onClick={handleAddWithoutInviting} disabled={saving} color="secondary">
                                <Trans>Add without inviting</Trans>
                            </Button>
                        )}

                        {editMode && (
                            <Button onClick={handleAddWithoutInviting} disabled={saving} color="secondary">
                                <Trans>Update buyer</Trans>
                            </Button>
                        )}

                        <Button disabled={saving} type="submit" color="primary" loading={saving}>
                            {editMode ? (
                                <Trans>Update and send invitation</Trans>
                            ) : (
                                <Plural one="Send invitation" other="Send invitations" value={invites.length} />
                            )}
                        </Button>
                    </ModalFooter>
                </ValidationForm>
            )}

            <YesNoDialog
                show={showConfirmDialog}
                title={<Trans>Update and send invitation</Trans>}
                body={
                    <div>
                        <p>
                            <Trans>
                                Are you sure you want to update the buyer information and send an invitation to{' '}
                                {invites[0]?.inviteeFirstName} {invites[0]?.inviteeLastName}?
                            </Trans>
                        </p>
                        <p>
                            <Trans>The invitation will be sent to:</Trans>
                        </p>
                        <ul>
                            <li>
                                <Trans>Email: {invites[0]?.inviteeEmail}</Trans>
                            </li>
                            {invites[0]?.sendSms && invites[0]?.inviteePhoneNumber && (
                                <li>
                                    <Trans>SMS: {invites[0]?.inviteePhoneNumber}</Trans>
                                </li>
                            )}
                        </ul>
                    </div>
                }
                onAccept={() => {
                    setShowConfirmDialog(false);
                    sendRequest(true);
                }}
                onClose={() => setShowConfirmDialog(false)}
                confirmButtonText={<Trans>Update and send</Trans>}
                cancelButtonText={<Trans>Cancel</Trans>}
            />

            <YesNoDialog
                show={showSendInviteConfirmDialog}
                title={<Trans>Caution</Trans>}
                body={
                    <Plural
                        value={invites.length}
                        one="By proceeding you'll send an invitation to the buyer, do you want to continue?"
                        other="By proceeding you'll send an invitation to # buyers, do you want to continue?"
                    />
                }
                onAccept={() => {
                    setShowSendInviteConfirmDialog(false);
                    sendRequest(true);
                }}
                onClose={() => setShowSendInviteConfirmDialog(false)}
                confirmButtonText={<Trans>Continue</Trans>}
                cancelButtonText={<Trans>Cancel</Trans>}
            />
        </Modal>
    );
};

export default ResidentialUnitInviteBuyerDialog;
